import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import {
  getStoreCreditBalance,
  getStoreCreditTransactionHistory,
  StoreCreditBalance,
} from "@redotech/redo-customer-portal-app/api";
import { CustomerContext } from "@redotech/redo-customer-portal-app/contexts/CustomerContext";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { GetStoreCreditTransactionResponseBody } from "@redotech/redo-model/customer-wallet/customer-wallet-definition";
import { createContext, useContext, useState } from "react";

interface StoreCreditContextValue {
  storeCreditTransactionsLoad: LoadState<GetStoreCreditTransactionResponseBody>;
  storeCreditBalanceLoad: LoadState<StoreCreditBalance>;
  reload: () => void;
}
export const StoreCreditContext = createContext<
  StoreCreditContextValue | undefined
>(undefined);

export const StoreCreditContextProvider = ({ children }) => {
  const customer = useContext(CustomerContext);
  const customerEmail = getPrimaryCustomerEmail(customer);
  const [reloadStoreCreditTrigger, setReloadStoreCreditTrigger] =
    useState(Symbol());

  const storeCreditTransactionsLoad = useLoad(
    async (signal) => {
      if (customerEmail) {
        const storeCreditTransactions = await getStoreCreditTransactionHistory({
          customerId: customer._id,
          signal,
        });

        return storeCreditTransactions;
      } else {
        return undefined;
      }
    },
    [customer, reloadStoreCreditTrigger],
  );

  const storeCreditBalanceLoad = useLoad(
    async (signal) => {
      if (customerEmail) {
        const storeCreditBalance = await getStoreCreditBalance({
          customerId: customer._id,
          signal,
        });

        return storeCreditBalance;
      } else {
        return undefined;
      }
    },
    [customer, reloadStoreCreditTrigger],
  );

  const reloadStoreCredit = useHandler(() =>
    setReloadStoreCreditTrigger(Symbol()),
  );

  const value: StoreCreditContextValue = {
    storeCreditTransactionsLoad,
    storeCreditBalanceLoad,
    reload: reloadStoreCredit,
  };

  return (
    <StoreCreditContext.Provider value={value}>
      {children}
    </StoreCreditContext.Provider>
  );
};
